import { gql } from "@apollo/client";
import { PageContentStack } from "atoms/Stack";
import { ONSEN_ENV, ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX } from "helpers/environment";
import { gaSetSharedEventParams } from "helpers/gtag";
import useData from "helpers/useData";
import useEnforceAuth from "helpers/useEnforceAuth";
import useRouteState from "helpers/useRouteState";
import Message from "molecules/Message";
import React, { useEffect, useMemo, useState } from "react";
import NotFoundPage from "shared/NotFoundPage";
import PageTitle from "shared/PageTitle";
import UnauthorisedPage from "shared/UnauthorisedPage";

import BoxScore, { BoxScoreFragment, BoxScoreGamePlayerFragment } from "./games/BoxScore";
import GameOverviewHighlights, {
  GameOverviewHighlightsFragment,
  GameOverviewHighlightsGamePlayerFragment,
} from "./games/GameOverviewHighlights";
import GameOverviewPageHeader, {
  GameOverviewPageHeaderFragment,
  GameOverviewPageHeaderGamePlayerFragment,
} from "./games/GameOverviewPageHeader";
import GameOverviewReels, {
  GameOverviewReelsFragment,
  GameOverviewReelsGamePlayerFragment,
} from "./games/GameOverviewReels";
import UpdateStubbedUserEmailModal from "./games/GameOverviewUpdateStubbedUserEmailModal";
import PointChart from "./games/PointChart";
import ShotChart from "./games/ShotChart";

const GameOverviewPageGamePlayerFragment = gql`
  fragment GameOverviewPageGamePlayerFragment on GamePlayer {
    id
    ...GameOverviewPageHeaderGamePlayerFragment
    ...GameOverviewReelsGamePlayerFragment
    ...GameOverviewHighlightsGamePlayerFragment
    ...BoxScoreGamePlayerFragment
  }
  ${GameOverviewPageHeaderGamePlayerFragment}
  ${GameOverviewReelsGamePlayerFragment}
  ${GameOverviewHighlightsGamePlayerFragment}
  ${BoxScoreGamePlayerFragment}
`;

export default function GameOverviewPage({ gameId }) {
  useEnforceAuth({ enforceAuthenticated: true });
  const [gamePlayerId, gamePlayerIdSet] = useRouteState("gamePlayerId");
  const [tab, tabSet] = useRouteState("tab");
  const [stubbedModalOpen, setStubbedModalOpen] = useState(false);
  const isPublicLeaguePage = useMemo(() => {
    if (typeof window === "undefined" || !ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX) {
      return false;
    }

    let locationHost = window.location.host;
    if (ONSEN_ENV === "test") {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const stubHost = urlSearchParams.get("_stub_location_host");
      if (stubHost) {
        locationHost = stubHost;
      }
    }

    return locationHost.endsWith(ONSEN_PUBLIC_LEAGUES_HOST_SUFFIX);
  }, []);

  const [currentUserData, { loading: loadingUser }] = useData(gql`
    query GameOverviewPage_current_user {
      currentUser {
        id
        email
        stubbed
      }
    }
  `);

  const [permissionData, { loading: loadingPermission }] = useData(
    gql`
      query GameOverviewPage_permission($gameId: ID!) {
        game(id: $gameId) {
          id
          canOnsenAccessVideo
        }
      }
    `,
    { gameId },
  );

  const [data, { loading: loadingGame }] = useData(
    gql`
      query GameOverviewPage($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
          }
          org {
            gaId
          }
          statsVisible
          ...GameOverviewPageHeaderFragment
          ...GameOverviewReelsFragment
          ...GameOverviewHighlightsFragment
          ...BoxScoreFragment
        }
      }
      ${GameOverviewPageHeaderFragment}
      ${GameOverviewReelsFragment}
      ${GameOverviewHighlightsFragment}
      ${BoxScoreFragment}
    `,
    { gameId },
    { skip: permissionData && !permissionData.game?.canOnsenAccessVideo },
  );

  const [dataDefaultGamePlayer, { loading: loadingDefaultPlayer }] = useData(
    gql`
      query GameOverviewPage_defaultGamePlayer($gameId: ID!) {
        game(id: $gameId) {
          id
          currentUserDefaultGamePlayer {
            id
            ...GameOverviewPageGamePlayerFragment
          }
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gameId },
    { skip: !!gamePlayerId },
  );

  const [dataGamePlayer, { loading: loadingPlayer }] = useData(
    gql`
      query GameOverviewPage_gamePlayer($gamePlayerId: ID!) {
        gamePlayer(id: $gamePlayerId) {
          id
          ...GameOverviewPageGamePlayerFragment
        }
      }
      ${GameOverviewPageGamePlayerFragment}
    `,
    { gamePlayerId },
    { skip: !gamePlayerId },
  );

  useEffect(() => {
    if (isPublicLeaguePage) {
      return;
    }
    if (currentUserData?.currentUser?.stubbed === true) {
      setStubbedModalOpen(true);
    }
  }, [currentUserData?.currentUser?.stubbed, isPublicLeaguePage]);

  useEffect(() => {
    const gaId = data?.game?.org?.gaId;
    if (!gaId) {
      return;
    }
    gaSetSharedEventParams({ org_id: gaId });
  }, [data?.game?.org?.gaId]);

  const gamePlayer = dataGamePlayer?.gamePlayer || dataDefaultGamePlayer?.game?.currentUserDefaultGamePlayer;
  const isLoading = loadingUser || loadingPermission || loadingGame || loadingPlayer || loadingDefaultPlayer;

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (permissionData?.game && !permissionData.game.canOnsenAccessVideo) {
    return <UnauthorisedPage currentUser={currentUserData?.currentUser} />;
  }

  if (!data?.game) {
    return <NotFoundPage />;
  }

  return (
    <>
      <PageTitle title="Game Overview" />
      {!isPublicLeaguePage && (
        <UpdateStubbedUserEmailModal open={stubbedModalOpen} onClose={() => setStubbedModalOpen(false)} />
      )}

      <GameOverviewPageHeader
        game={data?.game}
        gamePlayer={gamePlayer}
        gamePlayerIdSet={gamePlayerIdSet}
        tab={tab}
        tabSet={tabSet}
      />

      {data?.game?.statsVisible && (
        <PageContentStack>
          <>
            {gamePlayer && (
              <>
                {(!tab || tab === "reels") && <GameOverviewReels game={data?.game} gamePlayer={gamePlayer} />}
                {(!tab || tab === "highlights") && (
                  <GameOverviewHighlights game={data?.game} gameId={gameId} gamePlayer={gamePlayer} />
                )}
                {(!tab || tab === "shotchart") && <ShotChart gamePlayerId={gamePlayer?.id} />}
              </>
            )}
          </>
          {(!tab || tab === "boxscore") && <BoxScore game={data?.game} gamePlayer={gamePlayer} />}
          {(!tab || tab === "pointchart") && <PointChart gameId={gameId} />}
        </PageContentStack>
      )}

      {!data?.game?.statsVisible && (
        <PageContentStack>
          <Message>Let’s watch the game! You can add more reactions as you watch each moment.</Message>
        </PageContentStack>
      )}
    </>
  );
}
